import { useResidentStore } from '@/store';
import type { Factory } from '@/types/common/factory';
import type { User } from '@/types/common/user';

// 默认工厂列表（兜底使用）
export const factoryList: Factory[] = [
  { id: 100, name: '国内工厂' },
  { id: 915, name: '泰国工厂(VALOCITYCRAFT)' },
];

export const initialFactory = () => {
  if (localStorage.getItem('locale') === 'th-TH') {
    setFactoryCache(factoryList[1]);
    return factoryList[1];
  } else {
    setFactoryCache(factoryList[0]);
    return factoryList[0];
  }
};

export const setFactoryCache = (factory: Factory) => {
  localStorage.setItem('ctcFactory', JSON.stringify(factory));
};

/**
 * @description 根据用户所属组织获取可访问的工厂列表（异步）
 * 如果用户属于组织，则只返回用户所属的组织
 * 如果用户不属于组织，则返回固定工厂列表 + 组织管理系统中的所有组织
 */
export const getAccessibleFactories = async (user?: User): Promise<Factory[]> => {
  const residentStore = useResidentStore();

  try {
    // 从组织管理系统获取所有组织
    const allOrganizations = await residentStore.getAllOrganizations();

    // 转换为 Factory 格式
    const organizationFactories: Factory[] = allOrganizations.map((org) => ({
      id: org.orgId,
      name: org.name,
    }));

    if (!user) {
      // 如果没有用户信息，返回固定工厂列表 + 所有组织
      return [...factoryList, ...organizationFactories];
    }

    // 判断用户是否属于组织
    const isOrganizationUser = user.organizationIds !== undefined && user.organizationIds !== null;

    if (isOrganizationUser) {
      // 如果用户属于组织，只返回用户所属的组织
      // 先从固定工厂列表中查找
      const userFactories = factoryList.filter((factory) => user.organizationIds!.includes(factory.id));
      // 再从组织列表中查找
      const userOrgFactories = organizationFactories.filter((factory) => user.organizationIds!.includes(factory.id));
      // 合并去重（优先使用固定工厂列表中的名称）
      const factoryMap = new Map<number, Factory>();
      userFactories.forEach((f) => factoryMap.set(f.id, f));
      userOrgFactories.forEach((f) => {
        if (!factoryMap.has(f.id)) {
          factoryMap.set(f.id, f);
        }
      });
      return Array.from(factoryMap.values());
    } else {
      // 如果用户不属于组织，返回固定工厂列表 + 所有组织
      // 合并去重（优先使用固定工厂列表中的名称）
      const factoryMap = new Map<number, Factory>();
      factoryList.forEach((f) => factoryMap.set(f.id, f));
      organizationFactories.forEach((f) => {
        if (!factoryMap.has(f.id)) {
          factoryMap.set(f.id, f);
        }
      });
      return Array.from(factoryMap.values());
    }
  } catch (error) {
    // 如果获取失败，返回默认工厂列表
    return factoryList;
  }
};

/**
 * @description 根据用户所属组织获取表单中的组织选项（异步）
 * 用于表单中的 radio 或 select 组件
 */
export const getOrganizationOptions = async (user?: User) => {
  const factories = await getAccessibleFactories(user);
  return factories.map((factory) => ({
    label: factory.name,
    value: factory.id,
  }));
};
